import {
  defaultKeyOf,
  findDirectKeyMatch,
  KeyOf,
  MasterdataEntityDefinition,
  MasterdataEntityRegistry,
  MasterdataService,
} from '@softapps/core/masterdata';
import { SOFTLINE_AI_AGENT_MENTION_ENTITIES } from '../ai-agent.tokens';
import { mentionToolArgument } from '../functions/mention-tool-argument';
import { AiAgentClientTool } from '../types/client-tool.type';

export interface ResolveMasterdataArgs {
  entity: string;
  term: string;
  limit?: number;
}

const DEFAULT_LIMIT = 5;
const MAX_LIMIT = 20;

/**
 * Turns a name or number the user typed into a concrete record. `exact` is only
 * set when exactly one result carries that key — an ambiguous term returns
 * candidates so the model asks instead of guessing.
 */
export const resolveMasterdataTool: AiAgentClientTool<ResolveMasterdataArgs> = {
  name: 'resolve_masterdata',
  description:
    'Sucht einen Stammdatensatz (z.B. Kunde, Artikel, Produktgruppe) zu einem Namen oder einer Nummer und liefert dessen Schlüssel für Tool-Argumente.',
  parameters: {
    type: 'object',
    properties: {
      entity: {
        type: 'string',
        description: 'Name des Stammdaten-Typs, siehe list_masterdata_entities.',
      },
      term: { type: 'string', description: 'Suchbegriff oder Nummer.' },
      limit: { type: 'number', description: `Maximale Trefferzahl (Standard ${DEFAULT_LIMIT}).` },
    },
    required: ['entity', 'term'],
  },
  async execute(args, injector) {
    const allowed = injector.get(SOFTLINE_AI_AGENT_MENTION_ENTITIES);
    if (!allowed.includes(args.entity))
      return {
        error: `unbekannter Stammdaten-Typ '${args.entity}'`,
        entities: allowed,
      };

    const definition = injector.get(MasterdataEntityRegistry).find(args.entity);
    if (!definition) return { error: `unbekannter Stammdaten-Typ '${args.entity}'` };

    const limit = Math.min(args.limit ?? DEFAULT_LIMIT, MAX_LIMIT);
    const results = await injector
      .get(MasterdataService)
      .search(definition, args.term, { limit });

    const keyOf = (definition.keyWith ?? defaultKeyOf) as KeyOf<unknown>;
    const exact = findDirectKeyMatch(results, args.term, keyOf);

    return {
      entity: definition.name,
      exact: exact ? toCandidate(definition, exact, keyOf) : null,
      candidates: results.map((r) => toCandidate(definition, r, keyOf)),
    };
  },
};

function toCandidate(
  definition: MasterdataEntityDefinition<unknown>,
  dto: unknown,
  keyOf: KeyOf<unknown>
) {
  const key = String(keyOf(dto) ?? '');
  return {
    key,
    label: definition.displayWith(dto),
    toolArgument: mentionToolArgument(definition.name, key) ?? null,
  };
}
